import React, { useState } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { Button, Row, Col } from 'react-bootstrap';
import { FormikCheckBox, FormikInput, PhoneWithCode, Dropzone } from '../../components';
import { createDropZoneFileRequest } from '../../requests/dropzone';

const TicketForm = props => {
  const { errors, handleModalClose, setFieldValue, ticket, touched, values, isSubmitting } = props;
  const { phoneCountryCode } = ticket;
  const [descriptionCounter, setDescriptionCounter] = useState(0);
  const dispatch = useDispatch();

  const handleDescriptionChange = e => {
    setDescriptionCounter(e.target.value.length);
    setFieldValue(e.target.name, e.target.value);
  };

  const onDropUploadedFile = document => {
    document.map(doc => setFieldValue(`ticket[documentKeys][${doc.id}]`, doc.code));
  };

  const handleDestroyFile = ({ id }) => {
    delete values.ticket.documentKeys[id];
  };

  const handleOnDrop = (code, documents) => {
    const dropzoneFiles = {
      drop_zone_file: {
        code,
        documents
      }
    };
    return createDropZoneFileRequest({
      dispatch,
      params: dropzoneFiles,
      formData: true,
      successCallback: response => response
    });
  };

  return (
    <>
      <Form className="container">
        <Row>
          <Col md={6}>
            <Field name="ticket[employeeName]">
              {({ field }) => (
                <FormikInput
                  {...field}
                  abbr
                  disabled
                  label="Solicitado Por"
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col md={6}>
            <Field name="ticket[title]">
              {({ field }) => (
                <FormikInput
                  {...field}
                  abbr
                  label="Titulo del ticket"
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <PhoneWithCode
              countryCodeField="ticket[phoneCountryCode]"
              label="Teléfono"
              phoneField="ticket[phone]"
              workPhoneCountryCode={phoneCountryCode}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
            />
          </Col>
          <Col md={6}>
            <Row>
              <Col md={12}>
                <p className="ticket-false-label">Canal de Respuesta</p>
              </Col>
            </Row>
            <Row>
              <Col md={3} className="d-flex align-items-center">
                <Field name="ticket[viaEmail]">
                  {({ field }) => (
                    <FormikCheckBox
                      {...field}
                      custom
                      field={field}
                      label="Correo"
                      tooltipSize="14"
                      tooltipText="Canal de Correo"
                    />
                  )}
                </Field>
              </Col>
              <Col md={3} className="d-flex align-items-center">
                <Field name="ticket[viaPhone]">
                  {({ field }) => (
                    <FormikCheckBox
                      {...field}
                      custom
                      field={field}
                      label="Teléfono"
                      tooltipSize="14"
                      tooltipText="Canal de Teléfono"
                    />
                  )}
                </Field>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Field name="ticket[email]">
              {({ field }) => (
                <FormikInput
                  {...field}
                  abbr
                  label="Correo"
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Field name="ticket[description]">
              {({ field }) => (
                <FormikInput
                  {...field}
                  abbr
                  as="textarea"
                  label={`Detalle del Incidente (${descriptionCounter}/2000)`}
                  minInputHeight="150"
                  onChange={e => handleDescriptionChange(e)}
                  error={getIn(errors, field.name)}
                  touched={getIn(touched, field.name)}
                />
              )}
            </Field>
          </Col>
          <Col md={12}>
            <Dropzone
              fileAccept=".pdf .xls .doc .docx .rtf .xlsx .ppt .pptx .jpg .jpeg .gif .mp3 .mp4 .wmv .avi .png"
              maxSize={50000000}
              multiple
              customClass="long-dropzone"
              onDelete={handleDestroyFile}
              onDrop={handleOnDrop}
              onDropUploaded={onDropUploadedFile}
            />
          </Col>
        </Row>
        <Row className="justify-content-end mt-3">
          <Col md={{ span: 3, offset: 6 }}>
            <Button className="btn" variant="outline-info" block onClick={handleModalClose}>
              CANCELAR
            </Button>
          </Col>
          <Col md={3}>
            <Button type="submit" block disabled={isSubmitting}>
              CONFIRMAR
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};

const setInitialValues = props => {
  const { phoneCountryCode, phone, email, viaEmail, viaPhone, description, title } = props.ticket;
  const { employee } = props;

  return {
    ticket: {
      description,
      title,
      email,
      employeeId: employee.id,
      employeeName: employee.fullName,
      phone,
      phoneCountryCode,
      viaEmail,
      viaPhone
    }
  };
};

const validationSchema = Yup.object().shape({
  ticket: Yup.object().shape({
    description: Yup.string()
      .required('Debes ingresar el detalle del incidente')
      .max(2000, 'Máximo 2000 caracteres permitidos'),
    title: Yup.string()
      .required('Debes ingresar un titulo para el incidente')
      .max(100, 'Máximo 100 caracteres permitidos'),
    email: Yup.string()
      .required('Debes ingresar un correo')
      .email('El email que ingresaste no es válido'),
    employeeId: Yup.string().required('Debes ingresar un nombre'),
    phone: Yup.string()
      .when('phoneCountryCode', {
        is: val => val === '+56',
        then: Yup.string().length(9, 'Debe ser de 9 dígitos'),
        otherwise: Yup.string().min(3, 'Deben ser al menos 3 dígitos')
      })
      .when('viaPhone', {
        is: val => val,
        then: Yup.string().required('Debe ingresar un teléfono'),
        otherwise: Yup.string().nullable()
      }),
    viaEmail: Yup.boolean(),
    viaPhone: Yup.boolean()
  })
});

const handleSubmit = (values, { props }) => {
  const { formRequest } = props;
  formRequest(values);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit
})(TicketForm);
